import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session is managed via an httpOnly tcl_admin cookie set by the server.
// The JWT is held in memory state only — never written to localStorage.
// On page reload the /me endpoint (cookie-authenticated) issues a fresh token.

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 2FA state (used only for speaker access code flows, not admin login)
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [tempToken, setTempToken] = useState(null);

  const clearAuth = useCallback(() => {
    // Remove any legacy localStorage remnants (one-time cleanup)
    localStorage.removeItem('tcl_admin_token');
    localStorage.removeItem('tcl_admin');
    localStorage.removeItem('tcl_last_activity');
    setToken(null);
    setAdmin(null);
  }, []);

  // Restore session on mount via the httpOnly cookie (no localStorage read).
  // /me returns a fresh JWT alongside the admin profile — stored in state only.
  useEffect(() => {
    const restore = async () => {
      // Also clear any stale localStorage values from the old implementation
      localStorage.removeItem('tcl_admin_token');
      localStorage.removeItem('tcl_admin');
      try {
        const res = await fetch(`${API_URL}/api/admin/me`, {
          credentials: 'include',
        });
        if (!res.ok) {
          clearAuth();
          return;
        }
        const data = await res.json();
        setAdmin(data.admin);
        setToken(data.access_token);
      } catch {
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };
    restore();
  }, [clearAuth]);

  const login = async (email, password) => {
    setError(null);
    setIsLoading(true);
    setTwoFactorPending(false);
    setTempToken(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      // Handle non-OK responses first
      if (!response.ok) {
        // Try to get the error detail from response
        let errorMessage = 'Login failed';
        try {
          // Use text() first then parse to avoid stream issues
          const responseText = await response.text();
          if (responseText) {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (parseError) {
          // If we can't parse JSON, use status-specific messages
          if (response.status === 403) {
            errorMessage = 'Not authorised to access admin portal';
          } else if (response.status === 401) {
            errorMessage = 'Invalid email or password';
          } else if (response.status === 429) {
            errorMessage = 'Too many login attempts. Please try again later.';
          } else {
            errorMessage = response.statusText || errorMessage;
          }
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Check if 2FA is required
      if (data.requires_2fa) {
        setTwoFactorPending(true);
        setTempToken(data.temp_token);
        return { success: true, requires2FA: true };
      }
      
      // Store auth data in memory only — session persists via httpOnly cookie
      setToken(data.access_token);
      setAdmin(data.admin);
      
      return { success: true, admin: data.admin };
    } catch (e) {
      // Handle the "body stream already read" error gracefully
      let errorMessage = e.message || 'Login failed';
      if (errorMessage.includes('body stream already read') || errorMessage.includes('Response body is already used')) {
        errorMessage = 'Invalid email or password';
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };
  
  // Complete 2FA login
  const verify2FA = async (code) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login/2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          temp_token: tempToken,
          code 
        })
      });
      
      if (!response.ok) {
        let errorMessage = 'Verification failed';
        try {
          const responseText = await response.text();
          if (responseText) {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (parseError) {
          if (response.status === 401) {
            errorMessage = 'Invalid verification code';
          } else if (response.status === 429) {
            errorMessage = 'Too many attempts. Please try again later.';
          }
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Clear 2FA state
      setTwoFactorPending(false);
      setTempToken(null);
      
      // Store auth data in memory only
      setToken(data.access_token);
      setAdmin(data.admin);
      
      return { success: true, admin: data.admin };
    } catch (e) {
      let errorMessage = e.message || 'Verification failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };
  
  // Cancel 2FA login attempt
  const cancel2FA = () => {
    setTwoFactorPending(false);
    setTempToken(null);
    setError(null);
  };
  
  // Get 2FA status
  const get2FAStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/2fa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get 2FA status');
      }
      
      return await response.json();
    } catch (e) {
      console.error('Get 2FA status error:', e);
      return null;
    }
  };
  
  // Setup 2FA (get QR code)
  const setup2FA = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to setup 2FA');
      }
      
      return { success: true, ...(await response.json()) };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };
  
  // Verify 2FA setup (enable 2FA)
  const verifySetup2FA = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/2fa/verify-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Invalid verification code');
      }
      
      const data = await response.json();
      
      // Update admin state to reflect 2FA enabled
      if (admin) {
        const updatedAdmin = { ...admin, two_factor_enabled: true };
        setAdmin(updatedAdmin);
      }
      
      return { success: true, backup_codes: data.backup_codes };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };
  
  // Disable 2FA
  const disable2FA = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/2fa/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to disable 2FA');
      }
      
      // Update admin state to reflect 2FA disabled
      if (admin) {
        const updatedAdmin = { ...admin, two_factor_enabled: false };
        setAdmin(updatedAdmin);
      }
      
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };
  
  // Regenerate backup codes
  const regenerateBackupCodes = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/2fa/regenerate-backup-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to regenerate backup codes');
      }
      
      const data = await response.json();
      return { success: true, backup_codes: data.backup_codes };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };
  
  // Change password
  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to change password');
      }
      
      // Update admin state to reflect password change
      if (admin) {
        const updatedAdmin = { ...admin, must_change_password: false };
        setAdmin(updatedAdmin);
      }
      
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };
  
  // Get audit logs
  const getAuditLogs = async (page = 1, pageSize = 50, action = null, adminEmail = null) => {
    try {
      let url = `${API_URL}/api/admin/audit-logs?page=${page}&page_size=${pageSize}`;
      if (action) url += `&action=${action}`;
      if (adminEmail) url += `&admin_email=${encodeURIComponent(adminEmail)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      
      return await response.json();
    } catch (e) {
      console.error('Get audit logs error:', e);
      return null;
    }
  };
  
  // Get audit log actions (for filtering)
  const getAuditLogActions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/audit-logs/actions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch audit log actions');
      }
      
      const data = await response.json();
      return data.actions;
    } catch (e) {
      console.error('Get audit log actions error:', e);
      return [];
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      clearAuth();
    }
  };

  const verifyInvite = async (inviteToken) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify-invite/${inviteToken}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Invalid invite token');
        }
        if (response.status === 400) {
          throw new Error('This invite has expired or already been used');
        }
        throw new Error('Invalid invite');
      }
      
      const data = await response.json();
      return { success: true, ...data };
    } catch (e) {
      let errorMessage = e.message || 'Invalid invite';
      if (errorMessage.includes('body stream already read') || errorMessage.includes('Response body is already used')) {
        errorMessage = 'Invalid invite token';
      }
      return { success: false, error: errorMessage };
    }
  };

  const setPassword = async (inviteToken, password, confirmPassword) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: inviteToken,
          password,
          confirm_password: confirmPassword
        })
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Invalid invite token');
        }
        if (response.status === 400) {
          // Try to get the specific error message
          try {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to set password');
          } catch (jsonError) {
            throw new Error('Failed to set password');
          }
        }
        throw new Error('Failed to set password');
      }
      
      const data = await response.json();
      
      // Auto-login after setting password — cookie set by backend
      setToken(data.access_token);
      setAdmin(data.admin);
      
      return { success: true, admin: data.admin };
    } catch (e) {
      let errorMessage = e.message || 'Failed to set password';
      if (errorMessage.includes('body stream already read') || errorMessage.includes('Response body is already used')) {
        errorMessage = 'Failed to set password. Please try again.';
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const createInvite = async (inviteData) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(inviteData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create invite');
      }
      
      return { success: true, invite: data };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const listAdmins = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/admins`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch admins');
      }
      
      return { success: true, ...data };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const listInvites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/invites`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch invites');
      }
      
      return { success: true, ...data };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const isAuthenticated = useCallback(() => {
    return !!admin && !!token;
  }, [admin, token]);

  const isAdmin = useCallback(() => {
    return admin && ['ADMIN', 'SUPER_ADMIN'].includes(admin.role);
  }, [admin]);

  const isSuperAdmin = useCallback(() => {
    return admin && admin.role === 'SUPER_ADMIN';
  }, [admin]);

  const hasRole = useCallback((requiredRoles) => {
    if (!admin) return false;
    return requiredRoles.includes(admin.role);
  }, [admin]);

  // Legacy function for course module access check (for Books page)
  const hasModuleAccess = useCallback(() => {
    // For now, return false as the premium module system isn't implemented
    // This is a placeholder that allows the Books page to render
    return false;
  }, []);

  // Function to check if user can view unpublished articles (for Articles page)
  const canViewUnpublishedArticles = useCallback(() => {
    // Only admins can view unpublished articles
    return admin && ['ADMIN', 'SUPER_ADMIN', 'EDITOR'].includes(admin.role);
  }, [admin]);

  return (
    <AuthContext.Provider value={{
      admin,
      user: admin, // Alias for compatibility
      token,
      isLoading,
      error,
      login,
      logout,
      verifyInvite,
      setPassword,
      createInvite,
      listAdmins,
      listInvites,
      isAuthenticated,
      isAdmin,
      isSuperAdmin,
      hasRole,
      hasModuleAccess,
      canViewUnpublishedArticles,
      // 2FA
      twoFactorPending,
      verify2FA,
      cancel2FA,
      get2FAStatus,
      setup2FA,
      verifySetup2FA,
      disable2FA,
      regenerateBackupCodes,
      // Password & Audit
      changePassword,
      getAuditLogs,
      getAuditLogActions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
